import {NextPage} from "next";
import React from "react";

import {NextReduxWrapper} from "src/store";
import {CareType, fetchCareEntry} from "src/utils/cms/cms";
import {fetchReasonBySlug} from "@services/api";
import {isReasonDiscoverable} from "workers/common/isReasonDiscoverable";
import {v5Pages} from "src/components/_common/_constants";
import {PracticeId} from "src/constants/practiceIds";
import {GetServerSidePropsResult} from "next";
import {Reason, Props as ReasonProps, getInitialPropsReason} from "./reason";
import {Topic, Props as TopicProps, getInitialPropsTopic} from "./topic";
import {withLocaleServerSideProps} from "@i18n/pages";

type Props = ReasonProps | TopicProps;

const renamedCareSlugs: Record<string, string> = {
  "allergy-symptoms-allergy-treatment": "allergy-symptoms",
  flu: "flu-shot",
  follow_up_pcp: "follow-up-pcp",
  "medication-refill": "medication-prescription-refill",
  mental_health_follow_up: "mental-health-follow-up",
  "pink-eye": "red-eye-pink-eye",
  "prescription-refill": "medication-prescription-refill",
  "routine-physical": "routine-physical-exam",
  "rx-refill": "medication-prescription-refill",
  rx: "medication-prescription-refill",
  "sinus-infection-symptoms": "sinus-infection",
  "sports-physical": "sports-physical-exam",
  "std-test": "sti-test",
  "urinary-tract-infection-uti-symptoms": "urinary-tract-infection",
  womens_health: "womens-health",
  "yeast-infection-symptoms": "yeast-infection",
};

const isTopicProps = (props: ReasonProps | TopicProps): props is TopicProps =>
  Boolean((props as TopicProps).content?.cares);

const CareItem: NextPage<Props> = (props: Props) =>
  isTopicProps(props) ? <Topic {...props} /> : <Reason {...(props as ReasonProps)} />;

const redirectToGetCareRoot = {
  redirect: {
    destination: v5Pages.getCareRoot,
    statusCode: 302,
  },
} satisfies GetServerSidePropsResult<Props>;

function redirectToDifferentGetCarePage(slug: string): GetServerSidePropsResult<Props> {
  return {
    redirect: {
      destination: `${v5Pages.getCareRoot}/${slug}`,
      statusCode: 301,
    },
  };
}

export const getServerSideProps = withLocaleServerSideProps(
  NextReduxWrapper.getServerSideProps<Props>(store => async context => {
    const {res, query} = context;

    const slug = query.slug as string;
    const type = query.type as string;
    const maybeNewSlug = renamedCareSlugs[slug];

    const careEntry = await fetchCareEntry(slug);

    if (careEntry?.type === CareType.TOPIC) {
      return {props: await getInitialPropsTopic({content: careEntry.content, store})};
    }

    const reason = await fetchReasonBySlug(maybeNewSlug || slug, PracticeId.CARBON);
    if (res) {
      if (!reason) {
        return redirectToGetCareRoot;
      }

      if (maybeNewSlug) {
        return redirectToDifferentGetCarePage(maybeNewSlug);
      }

      if (!careEntry || (!careEntry.content && !isReasonDiscoverable(reason))) {
        return redirectToGetCareRoot;
      }

      if (reason && type && type !== "get-care") {
        return redirectToDifferentGetCarePage(slug);
      }
    }

    if (reason && careEntry?.content) {
      return {props: await getInitialPropsReason({reason, reasonContent: careEntry.content})};
    }

    return redirectToGetCareRoot;
  }),
);

export default CareItem;
